<script setup>
import { ref, watch } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  systemPrompt: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['save']);

const dialogRef = ref(null);
const localPrompt = ref('');
const isSaving = ref(false);

watch(
  () => props.systemPrompt,
  newVal => {
    localPrompt.value = newVal || '';
  },
  { immediate: true }
);

const handleSave = async () => {
  isSaving.value = true;
  try {
    emit('save', localPrompt.value);
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    title="System Prompt"
    description="Define how your voice agent behaves, its personality, and the context it operates in."
    confirm-button-label="Save Changes"
    :is-loading="isSaving"
    :disable-confirm-button="isSaving"
    overflow-y-auto
    width="2xl"
    @confirm="handleSave"
  >
    <div class="flex flex-col gap-4">
      <TextArea
        v-model="localPrompt"
        label="System Prompt"
        placeholder="You are a helpful customer support voice agent. Your goal is to assist customers with their questions in a friendly and professional manner..."
        rows="12"
      />

      <div class="flex flex-col gap-2 p-4 rounded-lg bg-n-solid-3">
        <h4 class="text-sm font-medium text-n-slate-12">
          Tips for writing effective system prompts:
        </h4>
        <ul class="text-sm text-n-slate-11 space-y-1 list-disc list-inside">
          <li>Be specific about the agent's role and expertise</li>
          <li>Define the tone and personality you want</li>
          <li>Include any specific instructions or constraints</li>
          <li>Mention how to handle edge cases or uncertainties</li>
          <li>
            Use formatting tags like &lt;laugh&gt;, &lt;pause&gt;,
            &lt;emphasis&gt; for natural speech
          </li>
        </ul>
      </div>
    </div>
  </Dialog>
</template>
